/**
 * Режим «Навигация» (вейфайндинг) — типы, общие для API и веба.
 * Модель и решения — docs/wayfinding/plan.md, техника — docs/wayfinding/tech.md.
 */

/** Признак слоя режима в `layers.sourceConfig`. Тип слоя при этом остаётся `vector`. */
export const WAYFINDING_LAYER_FLAG = 'wayfindingLayer';

/** Ключ вида слоя режима там же. Отсутствует — читается как `signs` (см. ниже). */
export const WAYFINDING_LAYER_KIND = 'wayfindingKind';

/**
 * Тип носителя слоя указателей там же — id строки справочника (`WayfindingSignType.id`).
 * Отсутствует — у слоя типа нет, тип выбирается у каждого указателя. Читать через
 * `readWayfindingLayerSignTypeId`, тип указателя с учётом слоя — `resolveWayfindingSignTypeId`.
 */
export const WAYFINDING_LAYER_SIGN_TYPE = 'wayfindingSignType';

/** Вид слоя режима: носитель указателей либо объекты графа. */
export const WAYFINDING_LAYER_KINDS = ['signs', 'objects'] as const;
export type WayfindingLayerKind = (typeof WAYFINDING_LAYER_KINDS)[number];

/**
 * Слой режима — отдельный ВИД слоя для интерфейса при `type: 'vector'` в базе.
 *
 * Отдельный `type` в БД заводить нельзя: слой с неизвестным типом выпадает из конвейера
 * отрисовки (урок indoor, `docs/indoor/plan.md` §4.1.1). Поэтому вид опознаётся по флагу
 * в `sourceConfig` — и панель слоёв, и настройки слоя обязаны спрашивать именно эту
 * функцию, а не лезть в `sourceConfig` руками.
 *
 * Флаг НЕ означает «скрыт от публики»: слои режима отдаются наружу как любые другие
 * (docs/wayfinding/layers.md §2.3). Он означает ровно «слой режима» — открывает режим
 * карте, закрывает создание там, где режим не разрешён, и наследуется копиями.
 */
export function isWayfindingLayer(
  sourceConfig: Record<string, unknown> | null | undefined,
): boolean {
  return sourceConfig?.[WAYFINDING_LAYER_FLAG] === true;
}

/**
 * Вид слоя режима; `null` — слой не режимный.
 *
 * Вид — отдельный ключ, а не второй булев флаг рядом: два булевых допускают
 * бессмысленные комбинации (оба true, оба false), и разруливать их пришлось бы каждому
 * месту чтения. Отсутствие ключа читается как `signs` — слои указателей, созданные до
 * появления видов, остаются собой без миграции jsonb.
 */
export function wayfindingLayerKind(
  sourceConfig: Record<string, unknown> | null | undefined,
): WayfindingLayerKind | null {
  if (!isWayfindingLayer(sourceConfig)) {
    return null;
  }
  return sourceConfig?.[WAYFINDING_LAYER_KIND] === 'objects' ? 'objects' : 'signs';
}

/**
 * Этаж слоя в стопке карты: общие слои (0) ниже объектов (1), объекты ниже указателей (2).
 * Совпадает с порядком групп в панели режима (docs/wayfinding/layers.md §4.2).
 *
 * Вид слоя задаёт этаж ЖЁСТКО, а `sortOrder` решает только внутри этажа. Выравнивать
 * `sortOrder` при записи ненадёжно: слои рождаются десятком путей (кнопка «+», импорты,
 * интеграции, дубли, «слой из выделенного»), каждый кладёт новый слой наверх, и любой
 * пропущенный путь накрыл бы указатели подложкой. Правило чтения одно на всех.
 *
 * На обычных картах слоёв режима нет — все на нулевом этаже, порядок как был.
 */
export function layerStackTier(layer: {
  // `unknown`, а не Record: у вьювера `sourceConfig` — типизированный интерфейс без
  // индексной сигнатуры, и он в Record не приводится. Читается всё равно через
  // `wayfindingLayerKind`, которой не-объект не страшен.
  sourceConfig?: unknown;
}): number {
  const kind = wayfindingLayerKind(
    layer.sourceConfig as Record<string, unknown> | null | undefined,
  );
  return kind === 'signs' ? 2 : kind === 'objects' ? 1 : 0;
}

/**
 * Сравнение слоёв «снизу вверх» — порядок отрисовки: сначала этаж, внутри — `sortOrder`.
 * Панели, где верх списка = верх карты, сортируют обратным вызовом `(a, b) => cmp(b, a)`.
 * Равные `sortOrder` сравнение не различает: стабильная сортировка сохраняет порядок
 * ответа сервера (`sortOrder`, затем `createdAt`).
 */
export function compareLayersBottomToTop(
  a: { sourceConfig?: unknown; sortOrder?: number | null },
  b: { sourceConfig?: unknown; sortOrder?: number | null },
): number {
  return layerStackTier(a) - layerStackTier(b) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}

/** Ключ плана в `features.properties`. Отсутствует или чужой uuid → объект вне планов. */
export const FRAME_PROPERTY_KEY = '__frame';

/**
 * Преобразование страницы PDF, зафиксированное в момент растеризации.
 *
 * Без него экспорт промахивается: `pdfjs` строит вьюпорт по видимому боксу с учётом
 * `/Rotate` и `userUnit`, а `pdf-lib` меряет страницу по MediaBox. У чертежей из CAD
 * с ненулевым CropBox или поворотом это разные системы координат.
 */
export interface PdfPageTransform {
  /** Индекс страницы в исходном файле, с нуля. */
  pageIndex: number;
  /** `/Rotate` страницы: 0 | 90 | 180 | 270. */
  rotation: number;
  /** UserUnit страницы; по умолчанию 1. */
  userUnit: number;
  /** Бокс, по которому рендерили, в пунктах: [x, y, width, height]. */
  box: [number, number, number, number];
  /** Размер повёрнутого вьюпорта при scale = 1, в пунктах: [width, height]. */
  viewportPt: [number, number];
}

/** План (фрейм) — один загруженный лист. */
export interface WayfindingFrame {
  id: string;
  projectId: string;
  name: string;
  /** Позиция в колонке, с нуля. Уникальна в пределах карты. */
  slot: number;
  /**
   * Применённая раскладка: где фрейм стоит на канвасе прямо сейчас (левый верхний угол).
   * Источник правды для дельт при перестановке — см. tech.md §3.4.
   */
  originLng: number;
  originLat: number;
  /** Растровая подложка (сама картинка либо растеризованная страница PDF). */
  sourceUrl: string;
  widthPx: number;
  heightPx: number;
  /** Реальная ширина плана в метрах; задаёт размер фрейма на канвасе. */
  widthM: number;
  /** false — значение проставили мы: метрические подписи прячем. */
  widthMExplicit: boolean;
  /** Сдвиг подложки внутри фрейма в метрах. Модель есть, UI в v1 нет (plan.md §5.4). */
  offsetM: [number, number] | null;
  /** Исходный PDF, если план приехал PDF-ом. */
  originalUrl: string | null;
  pdfPage: PdfPageTransform | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Тип указателя — строка справочника карты (`project.settings.navigation.signTypes`).
 *
 * Тип описывает НОСИТЕЛЬ целиком: у всех указателей типа один код, одни габариты, один
 * цвет и одна иконка на карте, один генератор макета (docs/wayfinding/plan.md §9.1.1).
 *
 * Прежде (28.08) носитель описывал СЛОЙ, а справочник был отменён как второй источник
 * правды. Решение развёрнуто 11.09: указатели разных носителей живут в одном слое, и
 * носитель на слой заставлял бы плодить слои ради видов. Источник правды снова один —
 * справочник.
 *
 * ✏️ 13.09 тип выбирается у СЛОЯ (`WAYFINDING_LAYER_SIGN_TYPE`), а у указателя — только на
 * всякий случай переопределением (`signType`). Оба ссылаются на справочник, своих настроек
 * носителя нет ни у слоя, ни у указателя. У слоя тип необязателен: без него тип у каждого
 * указателя свой, как было 11.09.
 */
export interface WayfindingSignType {
  id: string;
  name: string;
  /** Код носителя для ведомости: «A», «F-2». */
  code?: string;
  /** Число сторон с содержанием, 1…`WAYFINDING_SIGN_MAX_SIDES`. */
  sides: number;
  /** Цвет маркера на карте и в выгрузке (hex): им красится фигура по умолчанию. */
  color: string;
  /**
   * Иконка маркера: встроенная фигура (`shape:<фигура>`, красится цветом типа) или своя
   * загруженная картинка (`custom:<url>`). Нет — фигура по числу сторон
   * (`wayfindingSignShape`). Эмодзи и стикеры типу не предлагаются (✏️ 11.09).
   */
  icon?: string;
  // Габаритов у типа нет (✏️ 11.09): размер различается даже у указателей одного типа.
  /** Носителю нужно электропитание (подсветка, экран) — колонка ведомости. */
  powered?: boolean;
  /** Материал носителя свободным текстом: «алюминий, порошковая окраска». */
  material?: string;
  /**
   * Генератор макета из общей ссылки карты на Автокрафт. Нет — у указателей типа макета
   * нет. Генератор выбирает ТИП, а не указатель: указатель только заполняет его поля.
   */
  autocraftGeneratorHash?: string;
  /**
   * Связи полей генератора с полями таблицы объектов (docs/wayfinding/plan.md §9.1.6):
   * ключ — ключ поля генератора (`dataModel`), значение — ИМЯ поля объекта («Название»,
   * «Номер»). Поля без записи не связаны — это умолчание.
   *
   * Связи у ТИПА, а не у указателя: генератор тоже у типа, и раскладка «какое поле макета
   * чем наполняется» одна на носитель. У каждого указателя это была бы та же таблица,
   * переписанная руками столько раз, сколько указателей.
   */
  autocraftFieldLinks?: Record<string, string>;
  /**
   * Где стоит ряд превью макетов относительно знака на карте — `icon-anchor` (какой частью
   * ряд стоит в точке). Нет — умолчание по числу сторон (`wayfindingSignDefaultPreviewAnchor`).
   * Общее правило задаёт ТИП: на одном плане соседствуют носители разной формы, и висящему над
   * проходом указателю превью нужно не там, где напольной стеле (docs/wayfinding/plan.md
   * §9.1.4). Слой и отдельный указатель могут переопределить его для себя
   * (`WAYFINDING_LAYER_PREVIEW_ANCHOR_KEY`, `WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY`).
   */
  previewAnchor?: string;
  /**
   * Свой якорь у отдельных страниц макета: ключ — номер страницы (1-based, строкой),
   * значение — `icon-anchor`. Страницу у стороны выбирает человек, и страницы одного
   * генератора бывает нужно развести по разным сторонам знака (у фингерпоста к каждой
   * стороне привязан двухстраничный макет). Страницы без записи встают по своей стороне.
   * Слой и указатель могут переопределить якорь отдельной страницы для себя
   * (`WAYFINDING_LAYER_PREVIEW_ANCHOR_BY_PAGE_KEY`, `WAYFINDING_SIGN_PREVIEW_ANCHOR_BY_PAGE_PROPERTY`).
   */
  previewAnchorByPage?: Record<string, string>;
  /**
   * Где стоит подпись указателя относительно знака на карте — `text-anchor` (какой частью
   * подпись стоит у знака), в тех же положениях, что якорь превью. Нет — под знаком
   * (`WAYFINDING_SIGN_DEFAULT_LABEL_ANCHOR`). Общее правило, как и у превью, задаёт ТИП: превью
   * и подпись делят место вокруг одного знака, и подписи нужна свободная от превью сторона. Слой
   * и отдельный указатель могут переопределить его для себя (`WAYFINDING_LAYER_LABEL_ANCHOR_KEY`,
   * `WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY`).
   */
  labelAnchor?: string;
  /**
   * Страница макета у каждой стороны многостороннего носителя с генератором: ключ — номер
   * стороны (1-based, строкой), значение — номер страницы. Нужна, когда в генераторе стороны
   * знака заложены страницами (стела: стр. 1 — лицо, стр. 2 — оборот), а данные у каждой
   * стороны свои (✏️ 28.09, Паша). Записи нет — страницу стороны тип не задаёт, остаётся
   * сохранённая у точки (по умолчанию первая); запись есть — сторона без ключа на первой.
   * У одностороннего не действует: там страницу выбирает сам указатель.
   */
  autocraftPageBySide?: Record<string, number>;
}

/**
 * Свойство фичи-указателя со ссылкой на тип (`WayfindingSignType.id`) — переопределение
 * типа слоя. Нет — указатель того типа, что выбран у слоя.
 */
export const WAYFINDING_SIGN_TYPE_PROPERTY = 'signType';

/**
 * Свойство фичи-указателя со своим якорем превью (`icon-anchor`) — переопределение якоря слоя и
 * типа. Нет — указатель следует за слоем, а если слой своего не задал, — за типом. Читать через
 * `readWayfindingSignPreviewAnchor`, итоговый якорь — `wayfindingSignPreviewEntries`.
 */
export const WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY = 'signPreviewAnchor';

/**
 * Свойство фичи-указателя со своими якорями страниц макета: ключ — номер страницы (1-based,
 * строкой), значение — `icon-anchor`. Переопределяет якорь страницы слоя и типа, как
 * `WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY` — общий якорь. Читать через
 * `readWayfindingSignPreviewAnchorByPage`.
 */
export const WAYFINDING_SIGN_PREVIEW_ANCHOR_BY_PAGE_PROPERTY = 'signPreviewAnchorByPage';

/**
 * Свойство фичи-указателя со своим якорем подписи (`text-anchor`) — переопределение якоря
 * подписи слоя и типа, как `WAYFINDING_SIGN_PREVIEW_ANCHOR_PROPERTY` у превью. Читать через
 * `readWayfindingSignLabelAnchor`; на карте его читает выражение `text-anchor` знака.
 */
export const WAYFINDING_SIGN_LABEL_ANCHOR_PROPERTY = 'signLabelAnchor';

/**
 * Угол поворота указателя, градусы по часовой от «вверх по плану». Им повёрнут знак на
 * карте (ручка поворота у выбранной точки, как у точек Автокрафта), и так же он повёрнут
 * на плане в выгрузке.
 */
export const WAYFINDING_SIGN_ORIENTATION_PROPERTY = 'signOrientation';

/**
 * Номер указателя — целое с единицы. Проставляется при создании указателя (следующий
 * после наибольшего в своей области нумерации) и уплотняется кнопкой «Пересчитать».
 * Читать через `readWayfindingSignNumber`: у старых указателей здесь лежит строка,
 * набранная руками («A-01»).
 */
export const WAYFINDING_SIGN_NUMBER_PROPERTY = 'signNumber';

/**
 * Своё название указателя — задано руками (переименование двойным кликом в списке слоёв) или
 * пришло с точкой из «Редактора»: имя, которое человек ей дал, при превращении в указатель
 * становится своим названием (`autocraftSignAdoptionPatch`). Непустое стоит поверх названия
 * по шаблону везде: в списке, в подписи на карте и в выгрузке. Пустое или ключа нет — название
 * по шаблону. Читать через `readWayfindingSignOwnName`.
 */
export const WAYFINDING_SIGN_OWN_NAME_PROPERTY = 'signName';

/**
 * Конечная точка указателя — `features.id` объекта, к которому он ведёт (docs/wayfinding/plan.md
 * §9.1.7). Одна на указатель, а не на сторону: связь нужна прежде всего табличкам на дверях.
 * Связанные поля макета наполняются из неё ссылками на объект, на карте связь видна
 * пунктиром. Двойное подчёркивание прячет ключ из карточки, таблицы и списков полей. Читать
 * через `readWayfindingSignDestination`.
 */
export const WAYFINDING_SIGN_DESTINATION_PROPERTY = '__wfDestination';

/**
 * Область нумерации указателей: `all` — сквозная по всей карте, `type` — своя внутри
 * каждого типа (у каждого типа — с единицы).
 */
export type WayfindingNumberingScope = 'all' | 'type';

/**
 * Название указателя в ДАННЫХ КАРТЫ — вычисленное свойство, в базу не пишется: его
 * подкладывают в копию данных, уходящую в карту (редактор и вьювер), а подпись слоя
 * указателей по умолчанию читает именно его. Двойное подчёркивание — признак служебного
 * ключа: списки свойств (поле подписи, таблица, карточка) его не показывают, а
 * `withFeatureName` не зеркалит в него имя нового объекта.
 */
export const WAYFINDING_SIGN_NAME_LABEL_PROPERTY = '__wfSignName';

/**
 * Название объекта слоя «Объекты» в ДАННЫХ КАРТЫ — вычисленное, как и название указателя:
 * поле названия слоя, а при штатном ключе — ещё и прежнее `name` (`readWayfindingObjectName`).
 * Его читает подпись слоя объектов по умолчанию (`withWayfindingObjectLabelDefaults`).
 */
export const WAYFINDING_OBJECT_NAME_LABEL_PROPERTY = '__wfObjectName';

/**
 * Превью макетов указателя в ДАННЫХ КАРТЫ — вычисленное, как и название: картинки сторон
 * по порядку, через перевод строки (в адресе его не бывает, а массив нормализация данных
 * карты склеила бы через запятую). Пустая строка — у указателя нет ни одного макета.
 * Читает его блок превью слоя указателей (`applySignTypeStyle`).
 */
export const WAYFINDING_SIGN_PREVIEWS_PROPERTY = '__wfSignPreviews';

/** Шаблон названия указателя по умолчанию — принятая у проектировщиков схема. */
export const DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE =
  '{typeCode}_{projectIndex}_{planNumber}_{signNumber}';

/**
 * Шаблон по умолчанию на ОБЫЧНОЙ карте (✏️ 19.09, Паша): указатели расставлены по базовой
 * карте, планов нет — номеру плана взяться неоткуда, и в названии оставался бы двойной
 * разделитель («A_12__5»).
 */
export const DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE_WITHOUT_PLANS = '{typeCode}_{projectIndex}_{signNumber}';

/**
 * Свой макет указателя — PDF, загруженный человеком, у указателей типа БЕЗ генератора
 * Автокрафта (у типов с генератором макет собирает Автокрафт). Растровое превью страниц
 * рендерит клиент при загрузке — сервер PDF не рисует.
 *
 * Ключ служебный (`__`): таблица, поле подписи и карточка его не показывают. Пишет его
 * ручка загрузки (`POST …/wayfinding/signs/:featureId/layout`) — ссылки в нём всегда на
 * НАШИ загрузки этой карты; снимает — обычная правка свойств (с undo), файлы при этом
 * остаются: на них могут ссылаться копии указателя.
 */
export const WAYFINDING_SIGN_LAYOUT_PROPERTY = '__signLayout';

export interface WayfindingSignLayout {
  pdfUrl: string;
  /** Превью первых страниц по порядку (`WAYFINDING_SIGN_LAYOUT_MAX_PREVIEW_PAGES`). */
  previewUrls: string[];
  /** Имя загруженного файла — для подписи в панели. */
  fileName: string;
  pageCount: number;
}

/** Сколько первых страниц своего макета рисуем превью — как столбик страниц Автокрафта. */
export const WAYFINDING_SIGN_LAYOUT_MAX_PREVIEW_PAGES = 4;

/** Потолок PDF своего макета: макет носителя — не чертёж, и общий лимит multipart тот же. */
export const MAX_SIGN_LAYOUT_PDF_BYTES = 50 * 1024 * 1024;

/** Потолок одной картинки превью: сторона ~1200 px в WebP весит сотни килобайт. */
export const MAX_SIGN_LAYOUT_PREVIEW_BYTES = 5 * 1024 * 1024;

/** Свой макет из свободных свойств фичи; `null` — макета нет или запись битая. */
export function readWayfindingSignLayout(
  properties: Record<string, unknown> | null | undefined,
): WayfindingSignLayout | null {
  const raw = properties?.[WAYFINDING_SIGN_LAYOUT_PROPERTY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const entry = raw as Record<string, unknown>;
  if (typeof entry.pdfUrl !== 'string' || entry.pdfUrl === '') {
    return null;
  }
  const previewUrls = Array.isArray(entry.previewUrls)
    ? entry.previewUrls.filter((url): url is string => typeof url === 'string' && url !== '')
    : [];
  return {
    pdfUrl: entry.pdfUrl,
    previewUrls,
    fileName: typeof entry.fileName === 'string' ? entry.fileName : '',
    pageCount:
      typeof entry.pageCount === 'number' && Number.isFinite(entry.pageCount) && entry.pageCount > 0
        ? Math.floor(entry.pageCount)
        : Math.max(1, previewUrls.length),
  };
}

export const WAYFINDING_SIGN_MAX_SIDES = 4;

/** Цвет маркера указателя без типа (или со ссылкой на удалённый тип). */
export const WAYFINDING_SIGN_UNTYPED_HEX = '#94a3b8';

/**
 * Фигура маркера по умолчанию — по числу сторон носителя, как условные знаки на планах
 * расстановки: 1 — прямоугольник (плоский носитель с одним лицом), 2 — два прямоугольника
 * друг над другом (двусторонний — это два односторонних спиной к спине), 3 — треугольник,
 * 4 — квадрат. Круг остаётся встроенной фигурой на выбор (✏️ 13.09: раньше круг был у
 * одностороннего, а прямоугольник — у двустороннего, и по знаку их было не отличить).
 */
export type WayfindingSignShape = 'circle' | 'rect' | 'rect-double' | 'triangle' | 'square';

export function wayfindingSignShape(sides: number): WayfindingSignShape {
  if (sides >= 4) {
    return 'square';
  }
  if (sides === 3) {
    return 'triangle';
  }
  if (sides === 2) {
    return 'rect-double';
  }
  return 'rect';
}

/**
 * Размер знака на карте: px или зум-выражение `interpolate`/`step` по `["zoom"]` со ступенями
 * в px — ровно то, что принимает `iconSizePx` блока. Читать через `readWayfindingMapSizes`.
 */
export type WayfindingMapSize = number | unknown[];

/**
 * Пункт содержания указателя.
 *
 * `text` — надпись. `ref` заполнит интеграция с Автокрафт (plan.md §9.1): модель обязана
 * пережить её появление без переделки, поэтому надпись — объект, а не голая строка.
 */
export interface WayfindingSignEntry {
  /** Направление стрелки в градусах, 0 — вверх по плану; null — без стрелки. */
  direction: number | null;
  text: string;
  ref?: { provider: string; externalId: string };
  /** Имя пиктограммы из набора символов карты. */
  pictogram?: string;
}

/** Настройки режима в `project.settings.navigation`. */
export interface WayfindingProjectSettings {
  /**
   * Режимом на этой карте уже работали. Ставится один раз при первом плане и делает
   * режим доступным даже после снятия флага с воркспейса (plan.md §4.3).
   */
  enabled?: boolean;
  /** Справочник типов указателей карты. Читать через `readWayfindingSignTypes`. */
  signTypes?: WayfindingSignType[];
  /**
   * Индекс проекта — переменная `{projectIndex}` в названиях указателей. Нумерация
   * и названия читаются через `readWayfindingNaming`.
   */
  projectIndex?: string;
  /** Область нумерации; нет — сквозная (`all`). */
  numberingScope?: WayfindingNumberingScope;
  /**
   * Нумерация начинается заново на каждом плане: область нумерации дополнительно делится по
   * плану указателя. Нет — один счёт на все планы.
   */
  numberingPerPlan?: boolean;
  /** Шаблон названия указателя; нет или пусто — `DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE`. */
  signNameTemplate?: string;
  /**
   * Размер знака указателя на карте (превью макетов — пропорционально ему). Нет —
   * `WAYFINDING_SIGN_MARKER_PX`. Общий на карту, а не у слоя: указатели всех слоёв на одном
   * плане обязаны быть одного масштаба.
   */
  signSize?: WayfindingMapSize;
  /** Размер точки объекта на карте (диаметр). Нет — `WAYFINDING_OBJECT_MARKER_PX`. */
  objectSize?: WayfindingMapSize;
  /**
   * Размер подписи указателя на карте. Нет — `WAYFINDING_SIGN_LABEL_PX`. Как и размер знака,
   * общий на карту; свой размер подписи у слоя (секция «Подписи») важнее.
   */
  signLabelSize?: WayfindingMapSize;
  /** Размер подписи объекта на карте. Нет — `WAYFINDING_OBJECT_LABEL_PX`. */
  objectLabelSize?: WayfindingMapSize;
  /**
   * Шаблон подписи объекта на карте: поля слоя «Объекты» в фигурных скобках («{Номер} {Название}»).
   * Нет или пусто — `DEFAULT_WAYFINDING_OBJECT_LABEL_TEMPLATE`, только название. Читать через
   * `readWayfindingObjectLabelTemplate`.
   */
  objectLabelTemplate?: string;
  /**
   * Легенды указателей на канвасе (редактор и публичная карта): справа от каждого плана и
   * общая под колонкой. Нет ключа — показаны; `false` — спрятаны. Читать через
   * `readWayfindingLegendSettings`.
   */
  canvasLegend?: boolean;
  /** Размер легенды на канвасе, % — множитель в масштабе плана. Нет — `WAYFINDING_LEGEND_SCALE_DEFAULT`. */
  legendScale?: number;
}

/** Что кладём в архив. */
export interface WayfindingExportParts {
  plans: boolean;
  specification: boolean;
  /**
   * Макеты указателей: PDF Автокрафта (недостающие собираются по данным точки во время
   * выгрузки) и свои загруженные PDF. Нет — задача из очереди до макетов, читается как `false`.
   */
  layouts?: boolean;
}

/**
 * Слои маркера на плане; каждый включается отдельно (plan.md §9.2). Знак повёрнут по углу
 * указателя всегда, как на карте: прежней галочки «стрелка направления» нет (✏️ 13.10), и
 * `arrow` от старых клиентов схема ручки отбрасывает.
 */
export interface WayfindingExportMarker {
  number: boolean;
  /**
   * Выноска с пунктами содержания (`signContent`). Галочка спрятана (✏️ 13.10): содержание
   * указателя больше нигде не вводится, и выноска выходила пустой.
   */
  callout: boolean;
  /**
   * Подписи прочих объектов плана (не указателей) — текстом и цветом подписи их слоя, как на
   * карте (✏️ 13.10). Нет — задача из очереди до этого, читается как `false`.
   */
  objectLabels?: boolean;
}

/**
 * Легенда (экспликация) в выгрузке планов (docs/wayfinding/legend.md). Обе галочки независимы:
 * своя у каждого плана, общая в конце или обе сразу.
 */
export interface WayfindingExportLegend {
  /** Легенда у каждого плана — по его указателям. */
  perPlan: boolean;
  /**
   * Где легенда плана: `side` — лист плана расширяется вправо и легенда стоит сбоку; `page` —
   * отдельной страницей того же формата сразу за планом.
   */
  placement: 'side' | 'page';
  /** Общая легенда по всем выгружаемым планам — отдельным файлом после планов. */
  summary: boolean;
}

/** Язык текстов внутри архива: шапки ведомости и легенды, отчёты, служебные имена файлов. */
export type WayfindingExportLanguage = 'ru' | 'en';

export interface WayfindingExportRequest {
  /** Планы; пусто или отсутствует — все. */
  frameIds?: string[];
  parts: WayfindingExportParts;
  /** `vector` — оригинал страницы PDF вектором; `raster` — подложка плана, как на канвасе. */
  planFormat: 'vector' | 'raster';
  marker: WayfindingExportMarker;
  /** Легенда к планам. Нет — без легенды (и задачи из очереди до неё). */
  legend?: WayfindingExportLegend;
  /** Язык текстов архива — язык интерфейса выгружающего. Нет — русский (вкладки и задачи до этого). */
  language?: WayfindingExportLanguage;
  /** Макеты — по папкам типов указателей (`layouts/<тип>/…`). Нет — все в `layouts/`. */
  layoutsByType?: boolean;
  /**
   * Планы файлами: `separate` — каждый план своим PDF, общая легенда — своим; `combined` — один
   * PDF-альбом: планы по порядку с их легендами и общая легенда в конце. Нет — `separate`.
   */
  planFiles?: 'separate' | 'combined';
}

export type WayfindingExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'canceled';

export interface WayfindingExportJob {
  id: string;
  status: WayfindingExportStatus;
  stage: string | null;
  done: number;
  total: number;
  url: string | null;
  error: string | null;
}

/**
 * Тип узла графа в свойствах фичи-объекта. Отсутствует — обычный проход.
 *
 * Узел графа — это ФИЧА слоя объектов, а не своя сущность (docs/wayfinding/layers.md §3):
 * геометрия, имя и план у неё штатные, своим остаётся только тип узла.
 */
export const WAYFINDING_NODE_TYPE_PROPERTY = '__wfType';

/**
 * Промежуточная точка графа — узел, который НЕ объект (docs/wayfinding/layers.md §3.8).
 *
 * Граф рисуется ломаными, и её вершины — просто места, где сходятся рёбра: названия, типа
 * и номера у них нет. Объект потом ставится на такую точку отдельным инструментом, и точка
 * СТАНОВИТСЯ объектом — меняется только этот тип, рёбра остаются на месте.
 *
 * Поэтому точка — та же фича слоя «Объекты», а не своя таблица: рёбра ссылаются на фичи,
 * и превращение в объект не требует перевешивать связи. Цена — всё, что читает объекты
 * (список, таблица, экспорт, публичная карта), обязано точки пропускать.
 */
export const WAYFINDING_WAYPOINT_NODE_TYPE = 'waypoint';

/** Тип, который получает объект, поставленный на промежуточную точку. */
export const WAYFINDING_OBJECT_NODE_TYPE = 'walk';

export function isWayfindingWaypoint(
  properties: Record<string, unknown> | null | undefined,
): boolean {
  return properties?.[WAYFINDING_NODE_TYPE_PROPERTY] === WAYFINDING_WAYPOINT_NODE_TYPE;
}

/**
 * Вершина пути, нарисованного инструментом «Ребро графа»: узел, который уже стоит в графе,
 * или новая промежуточная точка. Id новой точки задаёт клиент — так ответ не нужно
 * сопоставлять с запросом по порядку.
 */
export type WayfindingPathPoint =
  | { featureId: string }
  | { id: string; lng: number; lat: number };

/** Созданная промежуточная точка — фича в том же виде, что отдаёт ручка фич. */
export interface WayfindingPathFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: Record<string, unknown>;
}

export interface WayfindingPathResult {
  features: WayfindingPathFeature[];
  /** Созданные рёбра; уже существовавшие пары сюда не попадают. */
  edges: WayfindingEdge[];
}

/** Ребро — парой концов: id у ребра меняется, когда отмена его удаления создаёт его заново. */
export interface WayfindingEdgeEnds {
  aFeatureId: string;
  bFeatureId: string;
}

/**
 * Отмена пути «Ребра графа»: снять рёбра, которые он создал, и его новые промежуточные точки —
 * ровно то, что вернул `WayfindingPathResult`.
 *
 * Рёбра — парами концов, а не id: удалённое после пути ребро отмена удаления возвращает с НОВЫМ
 * id, и по старому id отмена пути его бы не нашла — ребро и его точки остались бы на карте.
 */
export interface WayfindingRemovePathRequest {
  layerId: string;
  edges: WayfindingEdgeEnds[];
  featureIds: string[];
}

/**
 * Какие точки ушли. Точка, к которой с тех пор пристроили другое ребро, остаётся: она уже
 * держит чужой путь.
 */
export interface WayfindingRemovePathResult {
  removedFeatureIds: string[];
}

/**
 * Всё, что удаление узла унесло, — ровно то, что вернёт отмена: сама фича (с прежним id
 * и местом в слое) и её рёбра, включая скрытые между планами.
 */
export interface WayfindingRemovedNode {
  feature: {
    id: string;
    layerId: string;
    geometry: { type: 'Point'; coordinates: [number, number] };
    properties: Record<string, unknown>;
    styleOverrides: Record<string, unknown> | null;
    sortOrder: number;
  };
  edges: Array<{ aFeatureId: string; bFeatureId: string }>;
}

/**
 * Узел убран из графа: его слой, ребро, склеившее путь через узел со степенью 2
 * (`null` — склеивать было нечего: тупик, развилка или такое ребро уже было), и то, что
 * удалено, — для отмены.
 */
export interface WayfindingRemoveNodeResult {
  layerId: string;
  edge: WayfindingEdge | null;
  removed: WayfindingRemovedNode;
}

/** Отмена удаления узла: вернуть узел с рёбрами и снять ребро, склеившее путь без него. */
export interface WayfindingRestoreNodeRequest extends WayfindingRemovedNode {
  removeEdgeId: string | null;
}

/**
 * Ребро графа — единственное, что осталось своей таблицей.
 *
 * В свойствах фич связь пришлось бы держать на обоих концах, и ничто не ловило бы ни
 * дубли, ни висячие ссылки после удаления объекта. FK с каскадом делает это бесплатно.
 *
 * Длины (`weightM`) в модели НЕТ: геометрию объекта правит общий редактор — рисованием,
 * перетаскиванием, массовым transform, импортом, undo, — и хранимая длина устаревала бы
 * на каждом из этих путей. Считается на чтении по координатам концов.
 */
export interface WayfindingEdge {
  id: string;
  projectId: string;
  /** Слой объектов, которому принадлежит ребро. Оба конца лежат в нём же. */
  layerId: string;
  aFeatureId: string;
  bFeatureId: string;
}
